// src/components/CodeInput/CodeInput.tsx
import { FC, useRef, useEffect, InputHTMLAttributes } from 'react';
import './CodeInput.scss';

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  onComplete?: (value: string) => void;
  className?: string;
}

const CodeInput: FC<CodeInputProps> = ({
                                         value,
                                         onChange,
                                         length = 6,
                                         error,
                                         disabled = false,
                                         autoFocus = true,
                                         placeholder = '',
                                         onComplete,
                                         className = '',
                                       }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount if autoFocus is true
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    // Trigger onComplete when code reaches full length
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const handleChange = (index: number, inputValue: string) => {
    if (disabled) return;

    // Only take the last character if multiple are pasted
    const newValue = inputValue.slice(-1);

    // Allow only digits
    if (newValue && !/^\d+$/.test(newValue)) return;

    const newCode = value.split('');
    newCode[index] = newValue;
    const newCodeString = newCode.join('');
    onChange(newCodeString);

    // Auto-focus next input
    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Handle backspace
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // Move to previous input and clear it
        const newCode = value.split('');
        newCode[index - 1] = '';
        onChange(newCode.join(''));
        inputRefs.current[index - 1]?.focus();
      } else if (value[index]) {
        // Clear current input
        const newCode = value.split('');
        newCode[index] = '';
        onChange(newCode.join(''));
      }
    }

    // Handle left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle right arrow
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length);

    // Check if pasted data contains only digits
    if (/^\d+$/.test(pastedData)) {
      // Pad with empty strings if needed
      const newCode = pastedData.padEnd(length, '').slice(0, length);
      onChange(newCode);

      // Focus the next empty input or last input
      const lastFilledIndex = Math.min(newCode.length, length) - 1;
      if (lastFilledIndex >= 0 && lastFilledIndex < length) {
        inputRefs.current[lastFilledIndex]?.focus();
      }
    }
  };

  const clearAll = () => {
    onChange('');
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  return (
    <div className={`code-input-component ${className}`}>
      <div className="code-inputs-container">
        {[...Array(length)].map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`code-digit 
              ${error ? 'error' : ''} 
              ${value[index] ? 'filled' : ''}
              ${disabled ? 'disabled' : ''}
            `}
            aria-label={`Code digit ${index + 1}`}
          />
        ))}
      </div>

      {error && <div className="code-input-error">{error}</div>}

      <div className="code-input-footer">
        <span className="code-input-hint">
          {placeholder}
        </span>
        {value.length === length && !error && (
          <button
            type="button"
            className="code-clear-button"
            onClick={clearAll}
          >
            Effacer
          </button>
        )}
      </div>
    </div>
  );
};

export default CodeInput;